/**
 * Inventory Hub Navigation Configuration
 * 
 * Uses @quotech/ui for unified navigation across all Quotech hubs.
 */

import {
  LayoutDashboard,
  Package,
  Layers,
  MapPin,
  ShoppingCart,
  ArrowLeftRight,
  Bell,
  BarChart,
  PieChart,
  Settings,
  Briefcase,
  Truck,
  Plus,
} from 'lucide-react'
import type { NavItem, ExternalLink, CommandItem, ModuleConfig, HubId, QuickAddItem } from '@quotech/ui'
import { getExternalLinks } from '@quotech/ui'

// =============================================================================
// MODULE CONFIG
// =============================================================================

export const MODULE_CONFIG: ModuleConfig = {
  id: 'inventory' as HubId,
  name: 'Inventory Hub',
  icon: Package,
  accentColor: 'stone',
  accentGradient: 'from-stone-500 to-stone-600',
  baseUrl: 'https://inventory.quotech.ai',
}

// =============================================================================
// MAIN NAVIGATION (Core Work)
// =============================================================================

export const NAV_ITEMS: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    href: '/',
  },
  {
    id: 'items',
    label: 'Items',
    icon: Package,
    href: '/items',
  },
  {
    id: 'stock',
    label: 'Stock Levels',
    icon: Layers,
    href: '/stock',
  },
  {
    id: 'locations',
    label: 'Locations',
    icon: MapPin,
    href: '/locations',
  },
  {
    id: 'orders',
    label: 'Orders',
    icon: ShoppingCart,
    href: '/orders',
  },
  {
    id: 'transfers',
    label: 'Transfers',
    icon: ArrowLeftRight,
    href: '/transfers',
  },
]

// =============================================================================
// SECONDARY NAVIGATION (Tools)
// =============================================================================

export const SECONDARY_NAV_ITEMS: NavItem[] = [
  {
    id: 'reorder',
    label: 'Reorder Alerts',
    icon: Bell,
    href: '/reorder',
  },
  {
    id: 'usage',
    label: 'Usage by Job',
    icon: BarChart,
    href: '/usage',
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: PieChart,
    href: '/reports',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    href: '/settings',
  },
]

// =============================================================================
// EXTERNAL HUB LINKS
// =============================================================================

export const EXTERNAL_LINKS: ExternalLink[] = getExternalLinks('inventory')

// =============================================================================
// QUICK ADD ITEMS (Header dropdown)
// =============================================================================

export const QUICK_ADD_ITEMS: QuickAddItem[] = [
  {
    id: 'new-item',
    label: 'New Item',
    icon: Plus,
    action: () => { window.location.href = '/items/new' },
    shortcut: '⌘⇧I',
  },
  {
    id: 'new-order',
    label: 'New Order',
    icon: ShoppingCart,
    action: () => { window.location.href = '/orders/new' },
  },
  {
    id: 'new-transfer',
    label: 'New Transfer',
    icon: ArrowLeftRight,
    action: () => { window.location.href = '/transfers/new' },
  },
]

// =============================================================================
// COMMAND PALETTE COMMANDS
// =============================================================================

export const COMMANDS: CommandItem[] = [
  // Actions
  {
    id: 'new-item',
    label: 'New Item',
    description: 'Add a new inventory item',
    icon: Plus,
    action: () => { window.location.href = '/items/new' },
    keywords: ['create', 'add', 'item', 'inventory'],
    category: 'action',
  },
  {
    id: 'new-order',
    label: 'New Order',
    description: 'Create a new order',
    icon: ShoppingCart,
    action: () => { window.location.href = '/orders/new' },
    keywords: ['create', 'order', 'purchase'],
    category: 'action',
  },
  
  // Navigation
  {
    id: 'go-dashboard',
    label: 'Go to Dashboard',
    icon: LayoutDashboard,
    action: () => { window.location.href = '/' },
    keywords: ['navigate', 'home', 'dashboard'],
    category: 'navigation',
  },
  {
    id: 'go-items',
    label: 'Go to Items',
    icon: Package,
    action: () => { window.location.href = '/items' },
    keywords: ['navigate', 'items', 'inventory'],
    category: 'navigation',
  },
  {
    id: 'go-stock',
    label: 'Go to Stock Levels',
    icon: Layers,
    action: () => { window.location.href = '/stock' },
    keywords: ['navigate', 'stock', 'levels'],
    category: 'navigation',
  },
  
  // Hub switching
  {
    id: 'switch-jobs',
    label: 'Switch to Jobs Hub',
    icon: Briefcase,
    action: () => { window.location.href = 'https://dashboard.quotech.ai' },
    keywords: ['hub', 'jobs', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-assets',
    label: 'Switch to Assets Hub',
    icon: Truck,
    action: () => { window.location.href = 'https://assets.quotech.ai' },
    keywords: ['hub', 'assets', 'switch'],
    category: 'hub',
  },
]

// =============================================================================
// HUB LINKS (for Command Palette)
// =============================================================================

export const HUB_LINKS: ExternalLink[] = EXTERNAL_LINKS

// =============================================================================
// ALIASES (for backward compatibility with setupApi.ts)
// =============================================================================

export const navigationItems = NAV_ITEMS
export const moduleConfig = MODULE_CONFIG
export const secondaryNavItems = SECONDARY_NAV_ITEMS
export const externalLinks = EXTERNAL_LINKS
export const commands = COMMANDS
export const hubLinks = HUB_LINKS
